import {
  Component,
  HostListener,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { entAnimations } from 'src/app/animations';
import { TeamGraph } from 'src/app/models/team-graph.model';
import { GraphService } from 'src/app/services/graph/graph.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { TeamsResponse } from 'src/app/models/services/graph/graph-service-response.model';
import { ToolBoxService } from 'src/app/services/toolbox/toolbox.service';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { RestoreTeamDialogComponent } from './restore-team-dialog/restore-team-dialog.component';
import { SuccesSnackbarComponent } from '../../snackbars/succes-snackbar/success-snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

@Component({
  selector: 'app-deleted-teams',
  templateUrl: './deleted-teams.component.html',
  styleUrl: './deleted-teams.component.scss',
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class DeletedTeamsComponent implements OnInit {
  @ViewChild(MatSort) sort: MatSort;
  isLoading = false;
  defaultColumns: string[] = [
    'timeDeletion',
    'displayName',
    'mail',
    'deletedDateTime',
    'daysBeforeDeletion',
    'restore',
  ];
  displayedColumnsTeams: string[] = [...this.defaultColumns];
  hiddenColumns: string[] = [];
  expandedTeam: TeamGraph | null;
  dataSourceTeams: MatTableDataSource<TeamGraph>;
  teamsNameFilter: FormControl = new FormControl('');
  teamsMailFilter: FormControl = new FormControl('');
  localStorageName = 'contextStorageTeams';
  hidePagination = false;
  isFilterActive = false;
  minDate: Date;
  maxDate: Date;
  restoredTeam: TeamGraph;
  range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  contextStorage: {
    pageIndex: number;
    pageSize: number;
    length: number;
    nextPageLink: string;
    savedDataSourceTeams: TeamGraph[];
    teamsNameFilterValue: string;
    teamsMailFilterValue: string;
  };

  @ViewChild(MatPaginator) paginator: MatPaginator;
  constructor(
    private _graphService: GraphService,
    private _router: Router,
    private _matDialog: MatDialog,
    private _localStorageService: LocalStorageService,
    private _toolboxService: ToolBoxService,
    private _snackBar: MatSnackBar,
    private _translateService: TranslateService
  ) {}

  ngOnInit() {
    this.maxDate = new Date();
    this.minDate = new Date();
    this.minDate.setDate(this.maxDate.getDate() - 31);
    this.setVisibleColumns();
    this.resetValues();
    this.getContextStorage();
    if (this.contextStorage.teamsNameFilterValue) {
      this.nameFilterValue = this.contextStorage.teamsNameFilterValue;
      this.filterTeams();
    } else {
      this.getTeamsPage();
    }
  }

  selection = new SelectionModel<TeamGraph>(true, []);

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSourceTeams.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSourceTeams.data);
  }

  onStartDateChange(event: MatDatepickerInputEvent<Date>) {
    this.startDate = event.value;
    this.endDate = null; 
  }

  onEndDateChange(event: MatDatepickerInputEvent<Date>) {
    if (event.value) {
      // Clone la date et force l'heure à 23:59:59.999
      this.endDate = new Date(event.value);
      this.endDate.setHours(23, 59, 59, 999);
    }    

    if(this.startDate && this.endDate) {
      debounceTime(500);
      this.filterTeams(true);
    }
  }

  resetValues(resetFilters = true) {
    this.isLoading = false;
    this.dataSourceTeams = null;
    this.teamsNameFilter = new FormControl('');
    this.teamsMailFilter = new FormControl('');
    this.range.reset();
    if (resetFilters) {
      this.isFilterActive = false;
    }
    this.resetContextStorage();
    this.teamsMailFilter.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.filterTeams(true);
      });

    this.teamsNameFilter.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.filterTeams(true);
      });
  }

  resetContextStorage() {
    this.contextStorage = {
      pageIndex: 0,
      pageSize: 10,
      length: null,
      nextPageLink: null,
      savedDataSourceTeams: [],
      teamsNameFilterValue: '',
      teamsMailFilterValue: '',
    };
  }

  async getTeamsPage(): Promise<void> {
    this.isLoading = true;
    await this._graphService
      .getDeletedTeamsPage(this.contextStorage.nextPageLink)
      .subscribe({
        next: response => {
          this.setDataSources(response);
          this._localStorageService.setItem(
            this.localStorageName,
            this.contextStorage
          );
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  openRestoreTeamDialog(team: TeamGraph) {
    const dialogRef = this._matDialog.open(RestoreTeamDialogComponent, {
      panelClass: 'app-dashboard-reset-confirm-dialog',
      disableClose: true,
      data: { team: team },
    });
    dialogRef.afterClosed().subscribe((response: boolean) => {
      if (response === true) {
        this.reloadTeams();
        this.restoredTeam = team;
        this.showSnackbar('success', 5000);
      }
    });
  }

  async getFilteredDeletedTeams(): Promise<void> {
    this.isLoading = true;
    await this._graphService
      .getFilteredDeletedTeams(this.nameFilterValue, this.mailFilterValue, this.startDate, this.endDate)
      .subscribe({
        next: response => {
          this.setDataSources(response);
        },
        error: () => {
          /**/
        },
      });
  }

  setFilter(resetPageIndex = false) {
    this.contextStorage.teamsNameFilterValue = this.nameFilterValue;
    this.contextStorage.teamsMailFilterValue = this.mailFilterValue;
    this.contextStorage.savedDataSourceTeams = [];
    this.dataSourceTeams = null;
    if (resetPageIndex) this.contextStorage.pageIndex = 0;
    this._localStorageService.setItem(
      this.localStorageName,
      this.contextStorage
    );
    this.isFilterActive = true;
  }

  async filterTeams(resetPageIndex = false) {
    this.setFilter(resetPageIndex);
    if (this.isFilterEmpty()) {
      this.reloadTeams(false);
      this.isFilterActive = false;
    } else {
      this.getFilteredDeletedTeams();
    }
  }

  reloadTeams(resetValues = true) {
    this._localStorageService.removeItem(this.localStorageName);
    this.resetValues(resetValues);
    this.getTeamsPage();
  }

  setDataSources(response: TeamsResponse) {
    if (response.paginator) {
      this.contextStorage.nextPageLink = response.paginator.nextPageLink;
      if (response.paginator.totalCount) {
        this.contextStorage.length = response.paginator.totalCount;
        this.hidePagination = false;
      } else if (response.paginator.totalCount === 0) {
        this.contextStorage.length = response.paginator.totalCount;
        this.hidePagination = true;
      }
    }
    if (!this.dataSourceTeams) {
      if (this.contextStorage.savedDataSourceTeams.length > 0) {
        this.dataSourceTeams = new MatTableDataSource<TeamGraph>(
          this.contextStorage.savedDataSourceTeams
        );
        this.contextStorage.savedDataSourceTeams = [];
      } else {
        this.dataSourceTeams = new MatTableDataSource<TeamGraph>(
          response.teams
        );
      }
    } else {
      this.dataSourceTeams.data = this.dataSourceTeams.data.concat(
        response.teams
      );
    }

    this.dataSourceTeams.paginator = this.paginator;
    setTimeout(() => {
      this.dataSourceTeams.paginator.length = this.contextStorage.length;
    });

    this.contextStorage.savedDataSourceTeams = this.dataSourceTeams.data;

    this.isLoading = false;
  }

  onPage(event: PageEvent): void {
    if (
      this.isFilterEmpty() &&
      event.pageIndex > this.contextStorage.pageIndex &&
      this.contextStorage.nextPageLink
    ) {
      this.contextStorage.pageIndex = event.pageIndex;
      this.getTeamsPage();
    } else {
      this.contextStorage.pageIndex = event.pageIndex;
    }
    this._localStorageService.setItem(
      this.localStorageName,
      this.contextStorage
    );
  }

  private async showSnackbar(type: 'success', milliseconds: number) {
    this._snackBar.dismiss();

    if (type == 'success') {
      this._snackBar.openFromComponent(SuccesSnackbarComponent, {
        data: this._translateService.instant(
          'DELETED-TEAMS-PAGE.RESTORED-TEAMS',
          { teamName: this.restoredTeam.displayName }
        ),
        duration: milliseconds,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    }
  }

  getContextStorage() {
    if (this._localStorageService.getItem(this.localStorageName)) {
      this.contextStorage = this._localStorageService.getItem(
        this.localStorageName
      );
    }
  }

  redirectToTeam(teamId: string) {
    this._router.navigate(['/teams/' + teamId]);
    this._localStorageService.removeItem('returnUrl');
  }

  isFilterEmpty(): boolean {
    return this.contextStorage.teamsNameFilterValue === '' && this.contextStorage.teamsMailFilterValue === '' && !this.startDate && !this.endDate;
  }

  isNullOrEmpty(value: string): boolean {
    return value === null || value === '';
  }

  getDaysBeforeDeletion(permanentlyDeletedDateTime: Date): number {
    const diffInMs =
      new Date(permanentlyDeletedDateTime).getTime() - Date.now();
    return Math.round(diffInMs / (1000 * 60 * 60 * 24));
  }

  getDeletionClass(permanentlyDeletedDateTime: Date) {
    const days = this.getDaysBeforeDeletion(permanentlyDeletedDateTime);
    if (days <= 10) {
      return 'deletedIn10DaysIcon';
    } else if (days <= 20) {
      return 'deletedIn2010DaysIcon';
    } else if (days <= 31) {
      return 'deletedIn3020DaysIcon';
    }
  }

  get nameFilterValue(): string {
    return this.teamsNameFilter.value;
  }

  set nameFilterValue(value: string) {
    this.teamsNameFilter.setValue(value);
  }

  get mailFilterValue(): string {
    return this.teamsMailFilter.value;
  }

  set mailFilterValue(value: string) {
    this.teamsMailFilter.setValue(value);
  }

  get startDate(): Date {
    return this.range.get('start').value;
  }

  set startDate(date: Date) {
    this.range.get('start').setValue(date);
  }

  get endDate(): Date {
    return this.range.get('end').value;
  }

  set endDate(date: Date)  {
    this.range.get('end').setValue(date);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setVisibleColumns();
  }

  setVisibleColumns() {
    this.displayedColumnsTeams = [...this.defaultColumns];
    this.hiddenColumns = [];
    this.expandedTeam = null;

    if (window.innerWidth < 1000) {
      this._toolboxService.hideTableColumn(
        'description',
        this.displayedColumnsTeams,
        this.hiddenColumns
      );
    }

    if (window.innerWidth < 940) {
      this._toolboxService.hideTableColumn(
        'mail',
        this.displayedColumnsTeams,
        this.hiddenColumns
      );
    }

    if (window.innerWidth < 580) {
      this._toolboxService.hideTableColumn(
        'deletedDateTime',
        this.displayedColumnsTeams,
        this.hiddenColumns
      );
    }

    if (window.innerWidth < 500) {
      this._toolboxService.hideTableColumn(
        'restore',
        this.displayedColumnsTeams,
        this.hiddenColumns
      );
    }

    if (window.innerWidth < 370) {
      this._toolboxService.hideTableColumn(
        'timeDeletion',
        this.displayedColumnsTeams,
        this.hiddenColumns
      );
    }
  }
}
